import React, { useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import LoadingScreen from '../common/LoadingScreen';
import ThemeSelector from '../ui/ThemeSelector';
import { useAnimation } from '../../context/AnimationContext';
import { useAuth } from '../../context/AuthContext';
import AppShell from './AppShell';

/**
 * MainLayout Component - Entry point for layout
 *
 * Phase 1 (Claude-style Sidebar):
 * - For authenticated routes (/, /editor/*, /settings): Render via AppShell (sidebar + content)
 * - For auth routes (/login, /register, /onboarding/*): Render children directly
 *
 * The old header with tabs has been REMOVED in favor of sidebar navigation.
 * Settings and navigation now live in the sidebar.
 */

// Routes where AppShell (sidebar) should NOT be used.
// `/account/inactive` (#559) es una pantalla de BLOQUEO, como las de onboarding y billing: a una
// cuenta suspendida no se le enseña una barra lateral navegable cuyos destinos van a devolverle
// 403 uno a uno, y que además compite con el único botón útil de la pantalla. Verificado en
// noprod el 2026-08-21: sin esta entrada, la pantalla se renderiza dentro del AppShell.
const NO_APPSHELL_ROUTES = ['/login', '/register', '/registro-freemium', '/account/inactive', '/status'];
const ONBOARDING_PREFIX = '/onboarding/';
const BILLING_PREFIX = '/billing/';
const ADMIN_PREFIX = '/admin';

const MainLayout = ({ children }) => {
  const location = useLocation();
  const { showInitialAnimation } = useAnimation();
  const { loading: authLoading, userProfile, inviteError, clearInviteError } = useAuth();
  const [themeSelectorOpen, setThemeSelectorOpen] = useState(false);

  // Post-checkout transition: while OnboardingGuard polls /api/billing/subscription
  // (hasta ~10 s, 10×1s), the URL is still `/?billing=success` so `pathname === '/'` would
  // normally render AppShell — leaking the sidebar behind a blank content area
  // until the guard resolves and Navigates to /onboarding/connect. Treat this
  // window as a billing/onboarding-style full-screen transition.
  const params = new URLSearchParams(location.search);
  const isPostCheckout = params.get('billing') === 'success';

  // Check if we should use AppShell (sidebar layout)
  // Admin routes use their own AdminLayout — skip AppShell to avoid double sidebar.
  const isAdminRoute =
    location.pathname === ADMIN_PREFIX || location.pathname.startsWith(ADMIN_PREFIX + '/');
  // PRP flash-diagnostics-onboarding-flow (2026-05-26, D6):
  // Never mount AppShell (sidebar) for a user mid-onboarding flow. Without this
  // check, a race where URL=`/` but profile.has_logged_in=false (e.g. between
  // OnboardingGuard navigates) briefly mounts AppShell and exposes sidebar —
  // the likely cause of the "flash of Settings" reported in F6 of the
  // invite-model PRP. We use the conservative combo `has_logged_in=false AND
  // onboarding_completed=false` to identify "currently in flow" — this protects
  // the 47 in-onboarding users without affecting 1 legacy user with logged_in=false
  // who already completed onboarding before that column existed.
  const isMidOnboarding =
    userProfile?.has_logged_in === false &&
    userProfile?.onboarding_completed === false;

  const shouldUseAppShell =
    !NO_APPSHELL_ROUTES.includes(location.pathname) &&
    !location.pathname.startsWith(ONBOARDING_PREFIX) &&
    !location.pathname.startsWith(BILLING_PREFIX) &&
    !isAdminRoute &&
    !isPostCheckout &&
    !isMidOnboarding;

  // Show loading screen during initial animation
  if (showInitialAnimation) {
    return <LoadingScreen />;
  }

  // On protected routes, don't render AppShell until auth+profile are resolved.
  // This prevents the sidebar from flashing before OnboardingGuard can redirect.
  if (shouldUseAppShell && authLoading) {
    return <LoadingScreen />;
  }

  // For authenticated routes: use AppShell with sidebar
  if (shouldUseAppShell) {
    return (
      <>
        {/* Development banner (commented out) */}
        {/* {process.env.REACT_APP_ENV === 'development' && <DevelopmentBanner />} */}

        <AppShell>
          {children}
        </AppShell>

        {/* Theme Selector Modal (available globally) */}
        <ThemeSelector
          open={themeSelectorOpen}
          onClose={() => setThemeSelectorOpen(false)}
        />

        {/* Invite email-mismatch error (Camino A post-confirmation) */}
        <Snackbar
          open={!!inviteError}
          onClose={clearInviteError}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={12000}
        >
          <Alert onClose={clearInviteError} severity="warning" sx={{ width: '100%' }}>
            {inviteError}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // For auth/onboarding routes: simple full-screen layout (no sidebar)
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        maxHeight: '100dvh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // #1722: ProviderStatusBanner publica su altura aquí; sin él vale 0 y nada cambia.
        boxSizing: 'border-box',
        paddingTop: 'var(--provider-banner-h, 0px)',
        transition: 'padding-top 200ms ease',
      }}
    >
      {/* Development banner (commented out) */}
      {/* {process.env.REACT_APP_ENV === 'development' && <DevelopmentBanner />} */}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Box>

      {/* Theme Selector Modal */}
      <ThemeSelector
        open={themeSelectorOpen}
        onClose={() => setThemeSelectorOpen(false)}
      />
    </Box>
  );
};

export default MainLayout;
